import { Component, computed, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { ModalService } from '../../../../core/services/modal.service';
import { YardService } from '../../../../core/services/yard.service';
import { GoogleMapsService } from '../../../../core/services/google-maps.service';
import { SegmentService, DriverConflict, EquipmentConflict } from '../../../../core/services/segment.service';
import { UserService } from '../../../../core/services/user.service';
import { AssetService } from '../../../../core/services/asset.service';
import { ToastService } from '../../../../shared/ui/toast/toast.service';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { FormCardComponent } from '../../../../shared/ui/forms/form-card/form-card.component';
import { FormFieldComponent } from '../../../../shared/ui/forms/form-field/form-field.component';
import { InputComponent } from '../../../../shared/ui/forms/input/input.component';
import { SelectComponent, SelectOption } from '../../../../shared/ui/forms/select/select.component';
import { SelectChipComponent, ChipOption } from '../../../../shared/ui/forms/select-chip/select-chip.component';
import { TextareaComponent } from '../../../../shared/ui/forms/textarea/textarea.component';
import { DatePickerComponent } from '../../../../shared/ui/forms/date-picker/date-picker.component';
import { TimePickerComponent } from '../../../../shared/ui/forms/time-picker/time-picker.component';
import {
  RadioCardGroupComponent,
  RadioOption,
} from '../../../../shared/ui/forms/radio-card-group/radio-card-group.component';
import { CheckboxComponent } from '../../../../shared/ui/forms/checkbox/checkbox.component';
import {
  ButtonGroupComponent,
  ButtonGroupOption,
} from '../../../../shared/ui/forms/button-group/button-group.component';
import { AddressFormComponent } from '../../../../shared/ui/forms/address-form/address-form.component';
import { HeadingComponent } from '../../../../shared/ui/typography/heading/heading.component';
import { TextComponent } from '../../../../shared/ui/typography/text/text.component';
import { BadgeComponent } from '../../../../shared/ui/badge/badge.component';
import { LucideIconComponent } from '../../../../shared/ui/lucide-icon/lucide-icon.component';
import { DriverCredentialWarningComponent } from '../../../../shared/ui/driver-credential-warning/driver-credential-warning.component';
import { Stop } from '../../../../core/models/stop.model';
import { Appointment } from '../../../../core/models/appointment.model';
import { Load } from '../../../../core/models/load.model';
import { Yard } from '../../../../core/models/yard.model';
import { Address } from '../../../../core/models/address.model';
import { ExchangeType, LoadSegment } from '../../../../core/models/load-segment.model';
import { BoundaryServicedBy } from '../../../../core/utils/itinerary-reconciler';
import { getLoadEndDate } from '../../../../core/utils/driver-credentials';

export type StopPurposeValue = 'pickup' | 'delivery' | 'both' | 'handoff' | 'yard_drop';

const PRESET_REASONS = [
  'Scheduled Relay',
  'HOS / Hours Limit',
  'Drop & Hook',
  'Vehicle Breakdown',
  'Driver Home Time',
] as const;

@Component({
  selector: 'app-add-stop-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    FormCardComponent,
    FormFieldComponent,
    InputComponent,
    SelectComponent,
    SelectChipComponent,
    TextareaComponent,
    DatePickerComponent,
    TimePickerComponent,
    RadioCardGroupComponent,
    CheckboxComponent,
    ButtonGroupComponent,
    AddressFormComponent,
    HeadingComponent,
    TextComponent,
    BadgeComponent,
    LucideIconComponent,
    DriverCredentialWarningComponent,
  ],
  templateUrl: './add-stop-modal.component.html',
})
export class AddStopModalComponent implements OnInit, OnDestroy {
  readonly hasCustomActions = true;

  private readonly fb = inject(FormBuilder);
  private readonly modalService = inject(ModalService);
  private readonly segmentService = inject(SegmentService);
  private readonly yardService = inject(YardService);
  private readonly userService = inject(UserService);
  private readonly assetService = inject(AssetService);
  private readonly googleMapsService = inject(GoogleMapsService);
  private readonly toastService = inject(ToastService);

  readonly isLoading = signal(false);
  readonly stopPurpose = signal<StopPurposeValue>('pickup');
  readonly locationType = signal<'saved' | 'custom'>('custom');
  readonly selectedYardId = signal<string | null>(null);
  readonly hasAppointment = signal(false);

  // Split / Relay state
  readonly splitHere = signal(false);
  readonly exchangeType = signal<ExchangeType>('full_swap');
  readonly isTeamOncoming = signal(false);
  readonly servicedBy = signal<BoundaryServicedBy>('outgoing');
  readonly selectedReasonChip = signal<string>('Scheduled Relay');
  readonly oncomingDriverId = signal<string | null>(null);
  readonly oncomingDriver2Id = signal<string | null>(null);
  readonly driverConflicts = signal<DriverConflict[]>([]);
  readonly truckConflicts = signal<EquipmentConflict[]>([]);

  form!: FormGroup;
  load!: Load;
  segments: LoadSegment[] = [];
  insertAfterStopNumber = 1;
  private subs = new Subscription();

  readonly reasonChipOptions: ChipOption[] = [
    ...PRESET_REASONS.map((r) => ({ value: r, label: r })),
    { value: 'Other', label: 'Other' },
  ];

  readonly locationTypeOptions: ButtonGroupOption[] = [
    { value: 'saved', label: 'Saved Yard / Terminal', icon: 'Warehouse' },
    { value: 'custom', label: 'Custom Address', icon: 'MapPin' },
  ];

  readonly exchangeTypeOptions: ButtonGroupOption[] = [
    { value: 'full_swap', label: 'Driver + Truck', icon: 'ArrowRightLeft' },
    { value: 'driver_only', label: 'Driver Only', icon: 'User' },
    { value: 'equipment_only', label: 'Equipment Only', icon: 'Truck' },
  ];

  readonly servicedByOptions: ButtonGroupOption[] = [
    { value: 'outgoing', label: 'Outgoing Driver (Before Swap)', icon: 'UserCheck' },
    { value: 'incoming', label: 'Oncoming Driver (After Swap)', icon: 'UserPlus' },
  ];

  readonly isIntermediateInsert = computed(() => {
    const totalStops = this.load?.stops?.length || 0;
    return this.insertAfterStopNumber >= 1 && this.insertAfterStopNumber < totalStops;
  });

  readonly purposeRadioOptions = computed<RadioOption[]>(() => {
    const intermediate = this.isIntermediateInsert();
    const cards: RadioOption[] = [
      {
        value: 'pickup',
        label: 'Pickup',
        description: 'Load freight at shipper',
        icon: 'ArrowUp',
      },
      {
        value: 'delivery',
        label: 'Delivery',
        description: 'Unload freight at receiver',
        icon: 'ArrowDown',
      },
      {
        value: 'both',
        label: 'Pick & Drop',
        description: 'Cross-dock or swap cargo',
        icon: 'ArrowUpDown',
      },
    ];

    if (intermediate) {
      cards.push({
        value: 'handoff',
        label: 'Relay / Handoff',
        description: 'Swap driver or equipment',
        icon: 'ArrowRightLeft',
      });
    } else {
      cards.push({
        value: 'yard_drop',
        label: 'Yard / Terminal',
        description: 'Drop or stage trailer at yard',
        icon: 'Warehouse',
      });
    }
    return cards;
  });

  readonly insertionContextText = computed(() => {
    const stops = this.load?.stops || [];
    const newNum = this.insertAfterStopNumber + 1;
    const prev = stops.find((s) => s.stopNumber === this.insertAfterStopNumber);
    const next = stops.find((s) => s.stopNumber === this.insertAfterStopNumber + 1);
    const prevName = prev?.address?.city
      ? `${prev.address.city}${prev.address.state ? ', ' + prev.address.state : ''}`
      : prev?.companyName || `Stop ${this.insertAfterStopNumber}`;
    if (next) {
      const nextName = next.address?.city
        ? `${next.address.city}${next.address.state ? ', ' + next.address.state : ''}`
        : next.companyName || `Stop ${next.stopNumber}`;
      return `Inserting as Stop #${newNum} between ${prevName} and ${nextName}`;
    }
    return `Adding as Stop #${newNum} after ${prevName}`;
  });

  readonly coveringSegment = computed<LoadSegment | null>(() => {
    const afterNum = this.insertAfterStopNumber;
    const seg =
      this.segments.find((s) => s.fromStopNumber <= afterNum && s.toStopNumber >= afterNum + 1) ||
      this.segments.find((s) => s.fromStopNumber <= afterNum && s.toStopNumber >= afterNum) ||
      null;
    return seg;
  });

  readonly outgoingDriverDisplay = computed(() => {
    const seg = this.coveringSegment();
    const dId = seg?.driverId || this.load?.driver1Id;
    if (!dId) return 'Unassigned';
    return this.userService.getDriverDisplayName(dId, seg?.driverName || this.load?.driver1Name);
  });

  readonly outgoingEquipmentDisplay = computed(() => {
    const seg = this.coveringSegment();
    const trk =
      seg?.truckUnitNumber ||
      (seg?.truckId ? this.assetService.getAssetName(seg.truckId) : '') ||
      (this.load?.truckId ? this.assetService.getAssetName(this.load.truckId) : '') ||
      'No Truck';
    const trl =
      seg?.trailerUnitNumber ||
      (seg?.trailerId ? this.assetService.getAssetName(seg.trailerId) : '') ||
      (this.load?.trailerId ? this.assetService.getAssetName(this.load.trailerId) : '') ||
      'No Trailer';
    return `${trk} / ${trl}`;
  });

  readonly hasSavedYards = computed(() => this.yardService.allYards().length > 0);

  readonly yardOptions = computed<SelectOption[]>(() =>
    this.yardService.allYards().map((yard) => ({
      value: yard.id,
      label: `${yard.name} (${yard.isTerminal ? 'Terminal' : 'Yard'})`,
      icon: yard.isTerminal ? 'Building' : 'Warehouse',
    }))
  );

  readonly selectedYard = computed<Yard | undefined>(() => {
    const id = this.selectedYardId();
    if (!id) return undefined;
    return this.yardService.allYards().find((y) => y.id === id);
  });

  readonly driverOptions = computed<SelectOption[]>(() =>
    this.userService
      .activeDrivers()
      .map((driver) => ({
        label: this.userService.getDriverDropdownLabel(driver),
        value: driver.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  );

  readonly truckOptions = computed<SelectOption[]>(() =>
    this.assetService
      .trucks()
      .map((truck) => ({
        label: truck.unitNumber || truck.licensePlate || truck.id,
        value: truck.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  );

  readonly trailerOptions = computed<SelectOption[]>(() =>
    this.assetService
      .trailers()
      .map((trailer) => {
        let label = trailer.unitNumber || trailer.licensePlate || trailer.id;
        if (trailer.specs?.trailerType) {
          label += ` - ${trailer.specs.trailerType}`;
        }
        return { label, value: trailer.id };
      })
      .sort((a, b) => a.label.localeCompare(b.label))
  );

  readonly loadEndDate = computed(() => getLoadEndDate(this.load?.stops || []));
  readonly selectedOncomingDriver = computed(
    () => this.userService.activeDrivers().find((d) => d.id === this.oncomingDriverId()) || null
  );
  readonly selectedOncomingDriver2 = computed(
    () => this.userService.activeDrivers().find((d) => d.id === this.oncomingDriver2Id()) || null
  );

  readonly isCommercialPurpose = computed(() => {
    const p = this.stopPurpose();
    return p === 'pickup' || p === 'delivery' || p === 'both';
  });

  constructor() {
    effect(() => {
      const yards = this.yardService.allYards();
      if (yards.length > 0 && this.locationType() === 'saved' && !this.selectedYardId()) {
        this.onYardSelect(yards[0].id);
      } else if (yards.length === 0 && this.locationType() === 'saved') {
        this.locationType.set('custom');
      }
    });
  }

  ngOnInit(): void {
    const modalData = this.modalService.state()?.data;
    if (modalData) {
      this.load = modalData.load;
      this.insertAfterStopNumber = modalData.insertAfterStopNumber ?? (this.load?.stops?.length || 1);
      this.segments = modalData.segments || [];
    }

    const initialPurpose: StopPurposeValue =
      modalData?.initialPurpose || (this.isIntermediateInsert() ? 'handoff' : 'delivery');

    const outgoingSeg = this.coveringSegment();
    const defaultTrailer = outgoingSeg?.trailerId || this.load?.trailerId || null;
    const defaultTruck = outgoingSeg?.truckId || this.load?.truckId || null;

    this.form = this.fb.group({
      companyName: ['', Validators.required],
      address: [null as Address | null, Validators.required],
      appointmentDate: [''],
      appointmentTime: [''],
      instructions: [''],
      notes: [''],
      // Split / Relay controls
      oncomingDriverId: [null],
      oncomingDriver2Id: [null],
      oncomingTruckId: [defaultTruck],
      oncomingTrailerId: [defaultTrailer],
      customExchangeReason: [''],
      exchangeNotes: [''],
    });

    this.subs.add(
      this.form.get('oncomingDriverId')!.valueChanges.subscribe((id) => {
        this.oncomingDriverId.set(id || null);
        void this.refreshConflictChecks();
      })
    );
    this.subs.add(
      this.form.get('oncomingDriver2Id')!.valueChanges.subscribe((id) => {
        this.oncomingDriver2Id.set(id || null);
      })
    );
    this.subs.add(
      this.form.get('oncomingTruckId')!.valueChanges.subscribe(() => {
        void this.refreshConflictChecks();
      })
    );
    this.subs.add(
      this.form.get('oncomingTrailerId')!.valueChanges.subscribe(() => {
        void this.refreshConflictChecks();
      })
    );

    if (this.segments.length === 0 && this.load?.id) {
      this.segmentService
        .getSegmentsForLoad(this.load.id)
        .then((segs) => {
          this.segments = segs;
        })
        .catch(() => {});
    }

    this.yardService
      .initialize()
      .then(() => {
        this.selectPurpose(initialPurpose);
      })
      .catch(() => {
        this.selectPurpose(initialPurpose);
      });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  selectPurpose(purpose: StopPurposeValue): void {
    this.stopPurpose.set(purpose);
    if (purpose === 'handoff') {
      this.splitHere.set(true);
      this.hasAppointment.set(false);
      if (this.hasSavedYards()) {
        this.onLocationTypeChange('saved');
      } else {
        this.locationType.set('custom');
      }
    } else if (purpose === 'yard_drop') {
      this.hasAppointment.set(false);
      if (this.hasSavedYards()) {
        this.onLocationTypeChange('saved');
      }
    } else {
      this.locationType.set('custom');
      this.servicedBy.set(purpose === 'pickup' ? 'incoming' : 'outgoing');
    }
    this.applySplitDefaults();
  }

  onToggleSplitHere(enabled: boolean): void {
    if (this.stopPurpose() === 'handoff') {
      this.splitHere.set(true);
      return;
    }
    this.splitHere.set(enabled);
    this.applySplitDefaults();
  }

  onExchangeTypeChange(type: string): void {
    this.exchangeType.set(type as ExchangeType);
    this.applySplitDefaults();
  }

  onToggleTeam(isTeam: boolean): void {
    this.isTeamOncoming.set(isTeam);
    if (!isTeam) {
      this.form.patchValue({ oncomingDriver2Id: null });
    }
  }

  onSelectReasonChip(chipValue: string | number): void {
    this.selectedReasonChip.set(String(chipValue));
  }

  private resolveExchangeReason(): string {
    const chip = this.selectedReasonChip();
    if (chip === 'Other') {
      return this.form.value.customExchangeReason?.trim() || 'Other';
    }
    return chip || 'Scheduled Relay';
  }

  private applySplitDefaults(): void {
    if (!this.form) return;
    const outgoingSeg = this.coveringSegment();
    const outDriver = outgoingSeg?.driverId || this.load?.driver1Id || null;
    const outTruck = outgoingSeg?.truckId || this.load?.truckId || null;
    const outTrailer = outgoingSeg?.trailerId || this.load?.trailerId || null;

    if (!this.splitHere()) return;

    const exType = this.exchangeType();
    if (exType === 'driver_only') {
      this.form.patchValue(
        {
          oncomingTruckId: outTruck,
          oncomingTrailerId: outTrailer,
        },
        { emitEvent: false }
      );
    } else if (exType === 'equipment_only') {
      this.form.patchValue(
        {
          oncomingDriverId: outDriver,
        },
        { emitEvent: false }
      );
      this.oncomingDriverId.set(outDriver);
    } else {
      if (!this.form.value.oncomingTrailerId && outTrailer) {
        this.form.patchValue({ oncomingTrailerId: outTrailer }, { emitEvent: false });
      }
    }
  }

  onLocationTypeChange(type: 'saved' | 'custom'): void {
    this.locationType.set(type);
    if (type === 'saved') {
      const yards = this.yardService.allYards();
      const currentYardId = this.selectedYardId() || (yards.length > 0 ? yards[0].id : null);
      if (currentYardId) {
        this.onYardSelect(currentYardId);
      }
    } else if (this.selectedYardId()) {
      this.selectedYardId.set(null);
      this.form.patchValue({
        companyName: '',
        address: null,
      });
    }
  }

  onYardSelect(yardId: string): void {
    this.selectedYardId.set(yardId);
    const yard = this.yardService.allYards().find((y) => y.id === yardId);
    if (yard) {
      this.form.patchValue({
        companyName: yard.name,
        address: yard.address,
      });
    }
  }

  formatCityStateZip(address?: Address | null): string {
    if (!address) return '';
    const cityState = [address.city, address.state].filter(Boolean).join(', ');
    return [cityState, address.zip].filter(Boolean).join(' ');
  }

  private async refreshConflictChecks(): Promise<void> {
    if (!this.splitHere() || !this.load) return;
    const val = this.form.getRawValue();
    const stops = this.load.stops || [];
    const startStr = stops[0]?.appointment?.from?.date;
    const endStr = stops[stops.length - 1]?.appointment?.from?.date;
    const startDate = startStr ? new Date(startStr) : new Date();
    const endDate = endStr
      ? new Date(endStr)
      : new Date(startDate.getTime() + 5 * 24 * 60 * 60 * 1000);

    try {
      if (val.oncomingDriverId && this.exchangeType() !== 'equipment_only') {
        const res = await this.segmentService.checkDriverAvailability(
          val.oncomingDriverId,
          startDate,
          endDate,
          this.load.id
        );
        this.driverConflicts.set(res.conflicts);
      } else {
        this.driverConflicts.set([]);
      }

      if (val.oncomingTruckId && this.exchangeType() !== 'driver_only') {
        const res = await this.segmentService.checkEquipmentAvailability(
          val.oncomingTruckId,
          'truck',
          startDate,
          endDate,
          this.load.id
        );
        this.truckConflicts.set(res.conflicts);
      } else {
        this.truckConflicts.set([]);
      }
    } catch {
      // Non-blocking conflict check
    }
  }

  async onSubmit(): Promise<void> {
    if (this.locationType() === 'saved' && this.hasSavedYards()) {
      const yard = this.selectedYard();
      if (!yard) {
        this.toastService.show('warning', 'Please select a yard or terminal', {
          title: 'Location Required',
        });
        return;
      }
      this.form.patchValue({
        companyName: yard.name,
        address: yard.address,
      });
    } else {
      const companyName = this.form.value.companyName?.trim();
      const addr = this.form.value.address;
      if (!companyName || !addr || (!addr.street && !addr.city && !addr.rawText)) {
        this.form.markAllAsTouched();
        this.toastService.show('warning', 'Please enter facility name and street address', {
          title: 'Location Required',
        });
        return;
      }
    }

    const val = this.form.getRawValue();
    const shouldSplit = this.isIntermediateInsert() && this.splitHere();

    if (shouldSplit) {
      const exType = this.exchangeType();
      if (exType !== 'equipment_only' && !val.oncomingDriverId) {
        this.toastService.show('warning', 'Please select the oncoming driver for the next leg.', {
          title: 'Driver Required',
        });
        return;
      }
      if (exType !== 'driver_only' && !val.oncomingTruckId) {
        this.toastService.show('warning', 'Please select the oncoming truck for the next leg.', {
          title: 'Truck Required',
        });
        return;
      }
      if (this.selectedReasonChip() === 'Other' && !val.customExchangeReason?.trim()) {
        this.toastService.show('warning', 'Please specify the reason when "Other" is selected.', {
          title: 'Reason Required',
        });
        return;
      }
    }

    const finalAddress: Address = { ...(val.address || {}) };
    if (!finalAddress.country) {
      finalAddress.country = 'USA';
    }

    const resolvedReason = this.resolveExchangeReason();

    this.isLoading.set(true);

    try {
      if (!finalAddress.lat || !finalAddress.lng) {
        try {
          const coords = await this.googleMapsService.geocodeAddress(finalAddress);
          if (coords) {
            finalAddress.lat = coords.lat;
            finalAddress.lng = coords.lng;
          }
        } catch (geoErr) {
          console.warn('Geocoding stop address skipped:', geoErr);
        }
      }

      let appointment: Appointment | null = null;
      if (this.hasAppointment() && val.appointmentDate) {
        appointment = {
          type: 'strict',
          from: {
            date: val.appointmentDate,
            time: val.appointmentTime || '08:00',
          },
          to: null,
        };
      }

      const purpose = this.stopPurpose();
      const newStop: Stop = {
        stopType: purpose,
        createdForRelay: purpose === 'handoff',
        companyName: val.companyName?.trim(),
        address: finalAddress,
        appointment,
        instructions: val.instructions?.trim() || null,
        notes: val.notes?.trim() || null,
        handoffReason: shouldSplit ? resolvedReason : null,
        requiresApproval: false,
        status: 'PENDING',
      };

      const outgoingSeg = this.coveringSegment();
      const effectiveDriverId =
        this.exchangeType() === 'equipment_only'
          ? outgoingSeg?.driverId || this.load.driver1Id || ''
          : val.oncomingDriverId || '';
      const effectiveTruckId =
        this.exchangeType() === 'driver_only'
          ? outgoingSeg?.truckId || this.load.truckId || ''
          : val.oncomingTruckId || '';
      const effectiveTrailerId =
        this.exchangeType() === 'driver_only'
          ? outgoingSeg?.trailerId || this.load.trailerId || ''
          : val.oncomingTrailerId || outgoingSeg?.trailerId || this.load.trailerId || '';

      await this.segmentService.insertStopAndReconcile(
        this.load,
        {
          newStop,
          afterStopNumber: this.insertAfterStopNumber,
          split: shouldSplit
            ? {
                oncomingLeg: {
                  driverId: effectiveDriverId,
                  driver2Id: this.isTeamOncoming() ? val.oncomingDriver2Id || undefined : undefined,
                  truckId: effectiveTruckId,
                  trailerId: effectiveTrailerId,
                  exchangeType: this.exchangeType(),
                  exchangeReason: resolvedReason,
                  exchangeNotes: val.exchangeNotes?.trim() || null,
                  exchangeLocation: finalAddress,
                },
                servicedBy: this.isCommercialPurpose() ? this.servicedBy() : 'none',
              }
            : null,
        },
        this.segments
      );

      this.toastService.show(
        'success',
        shouldSplit
          ? 'Stop added and oncoming leg assigned.'
          : 'Stop added to route.',
        { title: shouldSplit ? 'Route Split Scheduled' : 'Stop Added' }
      );
      const successCallback = this.modalService.state()?.onSuccess;
      if (successCallback) successCallback();
      this.modalService.close();
    } catch (err: any) {
      console.error('Failed to add stop:', err);
      this.toastService.show('error', err?.message || 'Failed to add stop', {
        title: 'Error',
      });
    } finally {
      this.isLoading.set(false);
    }
  }

  onCancel(): void {
    this.modalService.close();
  }
}
